import type {
    AccordionLineItem,
    Faq as FaqEntry,
    FaqQuestionHyperlinks,
    FaqStoreCards,
} from '../../../types/spaces/ecommerce-petsmart';
import { isResolvedLinkEntry } from '../../../utils/isResolvedLinkEntry';
import type { FaqItem, FaqProps } from './types';

const linkColumns = { desktop: 3, tablet: 2, mobile: 1 };

function hasContentType(
    entry: { sys: { type: string; contentType?: { sys: { id: string } } } } | null,
    contentType: string
): boolean {
    return entry?.sys.contentType?.sys.id === contentType;
}

function transformAccordionLineItem(entry: AccordionLineItem): FaqItem {
    return {
        id: entry.sys.id,
        title: entry.fields.question,
        content: {
            type: 'rich-text',
            document: entry.fields.answer,
        },
    };
}

function transformQuestionHyperlinks(entry: FaqQuestionHyperlinks): FaqItem {
    return {
        id: entry.sys.id,
        title: entry.fields.question,
        content: {
            type: 'links',
            links: entry.fields.answers.flatMap(answer => {
                if (!isResolvedLinkEntry(answer)) return [];

                const link = isResolvedLinkEntry(answer.fields.link) ? answer.fields.link : undefined;

                return [
                    {
                        id: answer.sys.id,
                        text: answer.fields.actionText,
                        href: link?.fields.webUrl,
                    },
                ];
            }),
        },
    };
}

function transformStoreCards(entry: FaqStoreCards): FaqItem {
    return {
        id: entry.sys.id,
        title: entry.fields.question,
        content: {
            type: 'stores',
            stores: entry.fields.storeCards.flatMap(storeCard => {
                if (!isResolvedLinkEntry(storeCard) || !isResolvedLinkEntry(storeCard.fields.cityState)) return [];

                const cityStateLink = isResolvedLinkEntry(storeCard.fields.cityState.fields.link)
                    ? storeCard.fields.cityState.fields.link
                    : undefined;
                const cta = isResolvedLinkEntry(storeCard.fields.cta) ? storeCard.fields.cta : undefined;
                const ctaLink = cta && isResolvedLinkEntry(cta.fields.link) ? cta.fields.link : undefined;

                return [
                    {
                        id: storeCard.sys.id,
                        cityState: {
                            actionText: storeCard.fields.cityState.fields.actionText,
                            link: { webUrl: cityStateLink?.fields.webUrl },
                        },
                        address: storeCard.fields.address,
                        phoneNumber: storeCard.fields.phoneNumber,
                        badges: storeCard.fields.badges?.flatMap(badge =>
                            isResolvedLinkEntry(badge) ? [{ text: badge.fields.text }] : []
                        ),
                        cta: cta
                            ? {
                                  behavior: cta.fields.behavior,
                                  text: cta.fields.text,
                                  link: { webUrl: ctaLink?.fields.webUrl },
                              }
                            : undefined,
                    },
                ];
            }),
        },
    };
}

export function transformFaq(entry: FaqEntry): FaqProps {
    const { headline, lineItem, subHeader, variant } = entry.fields;
    const props = { heading: headline, description: subHeader };

    switch (variant) {
        case 'FAQ':
            return {
                ...props,
                items: lineItem
                    .filter((item): item is AccordionLineItem => hasContentType(item, 'accordionLineItem'))
                    .map(transformAccordionLineItem),
            };
        case 'Columns':
            return {
                ...props,
                columns: linkColumns,
                items: lineItem
                    .filter((item): item is FaqQuestionHyperlinks => hasContentType(item, 'faqQuestionHyperlinks'))
                    .map(transformQuestionHyperlinks),
            };
        case 'Store Cards':
            return {
                ...props,
                items: lineItem
                    .filter((item): item is FaqStoreCards => hasContentType(item, 'faqStoreCards'))
                    .map(transformStoreCards),
            };
    }
}
